import type { Mat4 } from "../engine/math.js";

export const NAMEPLATE_MAX_DISTANCE = 35;
export interface NameplateTarget { readonly id: string; readonly name: string; readonly x: number; readonly y: number; readonly z: number }
export interface NameplateScreenPoint { readonly x: number; readonly y: number; readonly opacity: number }

export function projectNameplate(
  target: { readonly x: number; readonly y: number; readonly z: number },
  player: { readonly x: number; readonly z: number },
  matrix: Mat4,
  width: number,
  height: number,
): NameplateScreenPoint | null {
  const distance = Math.hypot(target.x - player.x, target.z - player.z);
  if (!Number.isFinite(distance) || distance > NAMEPLATE_MAX_DISTANCE || width <= 0 || height <= 0) return null;
  const { x, y, z } = target;
  const w = matrix[3]! * x + matrix[7]! * y + matrix[11]! * z + matrix[15]!;
  if (w <= 0) return null;
  const clipX = (matrix[0]! * x + matrix[4]! * y + matrix[8]! * z + matrix[12]!) / w;
  const clipY = (matrix[1]! * x + matrix[5]! * y + matrix[9]! * z + matrix[13]!) / w;
  const clipZ = (matrix[2]! * x + matrix[6]! * y + matrix[10]! * z + matrix[14]!) / w;
  if (![clipX, clipY, clipZ].every(Number.isFinite) || Math.abs(clipX) > 1 || Math.abs(clipY) > 1 || Math.abs(clipZ) > 1) return null;
  return { x: (clipX * 0.5 + 0.5) * width, y: (0.5 - clipY * 0.5) * height, opacity: Math.min(1, (NAMEPLATE_MAX_DISTANCE - distance) / 5) };
}

export class PlayerNameplates {
  private readonly layer = document.createElement("div");
  private readonly labels = new Map<string, HTMLElement>();
  constructor(parent: HTMLElement) { this.layer.className = "player-nameplates"; parent.append(this.layer); }

  render(targets: readonly NameplateTarget[], player: { readonly x: number; readonly z: number }, matrix: Mat4, width: number, height: number): void {
    const present = new Set<string>();
    const positions: NameplateScreenPoint[] = [];
    // Hard cap complements interest/distance culling in crowded public sessions.
    const nearest = [...targets].sort((a, b) => Math.hypot(a.x - player.x, a.z - player.z) - Math.hypot(b.x - player.x, b.z - player.z)).slice(0, 32);
    for (const target of nearest) {
      const point = projectNameplate({ ...target, y: target.y + 2.12 }, player, matrix, width, height);
      if (!point || positions.some((previous) => Math.abs(point.x - previous.x) < 72 && Math.abs(point.y - previous.y) < 18)) continue;
      positions.push(point);
      present.add(target.id);
      let label = this.labels.get(target.id);
      if (!label) {
        label = document.createElement("span");
        label.className = "player-nameplate";
        label.dataset.playerId = target.id;
        this.labels.set(target.id, label);
        this.layer.append(label);
      }
      label.textContent = target.name;
      label.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%, -100%)`;
      label.style.opacity = String(point.opacity);
    }
    for (const [id, label] of this.labels) if (!present.has(id)) { label.remove(); this.labels.delete(id); }
  }

  dispose(): void { this.layer.remove(); this.labels.clear(); }
}
